<template>
  <AppSheet :is-open="isOpen" mode="fullscreen" transparent :backdrop-dismiss="false"
    panel-class="reward-celebration-panel" backdrop-class="celebration-backdrop" @update:is-open="(v: boolean) => { if (!v) handleDismiss() }">
    <div class="flex items-center justify-center h-full">
      <div
        class="relative w-full h-full overflow-hidden flex flex-col items-center justify-between px-6 py-10 bg-warm">
        <!-- Decorative blurred halos behind everything -->
        <div class="absolute -top-20 -left-16 size-72 rounded-full opacity-40 pointer-events-none"
          :style="{ backgroundColor: palette.hex200, filter: 'blur(60px)' }" aria-hidden="true" />
        <div class="absolute -bottom-24 -right-16 size-80 rounded-full opacity-40 pointer-events-none"
          :style="{ backgroundColor: palette.hex300, filter: 'blur(70px)' }" aria-hidden="true" />

        <!-- Top: eyebrow -->
        <div class="relative z-10 w-full text-center pt-4">
          <div v-if="props.isOnboarding" class="flex justify-center mb-3">
            <div
              class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-brand-soft text-brand-deep text-xs font-medium">
              <LucideIcon name="Sparkles" :size="14" class="text-brand-deep" />
              {{ $t('onboarding.step6.celebration.exampleBadge') }}
            </div>
          </div>
          <div class="text-[11px] uppercase tracking-[0.12em] font-semibold" :style="{ color: palette.hex900 }">
            {{ eyebrowLabel }}
          </div>
        </div>

        <!-- Center: ConcentricCirclesImage receptacle + title + subtitle -->
        <div class="relative z-10 w-full flex flex-col items-center my-2">
          <ConcentricCirclesImage
            :palette-hex="{ hex200: palette.hex200, hex300: palette.hex300, hex400: palette.hex500 }"
            :image-src="showCentralAvatarImage ? avatarImage : undefined" :image-alt="avatarName">
            <span v-if="showCentralLevelNumber" class="ob-display font-bold text-[80px] leading-none"
              :style="{ color: palette.hex900 }">
              {{ props.notification?.level }}
            </span>
            <LucideIcon v-else-if="!showCentralAvatarImage" :name="centralIconName" :size="80"
              :style="{ color: palette.hex900 }" />
          </ConcentricCirclesImage>

          <h2 class="mt-2 text-center font-bold text-[26px] leading-tight text-ink">
            {{ titleMain }}
          </h2>
          <p class="ob-display text-[26px] leading-tight text-center" :style="{ color: palette.hex900 }">
            {{ titleVerb }}
          </p>

          <p v-if="subtitle" class="mt-3 text-center text-[14px] text-ink-3 leading-relaxed max-w-xs mx-auto">
            {{ subtitle }}
          </p>

        </div>

        <!-- Bottom: button + footer hint -->
        <div class="relative z-10 w-full flex flex-col items-center gap-3">
          <button type="button" data-testid="reward-celebration-close"
            class="!w-full !max-w-md !rounded-full !py-3.5 !text-white !font-semibold !text-[15px] !text-center !shadow-lg !active:opacity-90 !transition-opacity flex items-center justify-center"
            :style="{ backgroundColor: palette.hex900 }" @click="handleButtonClick">
            <template v-if="hasMoreNotifications">
              {{ $t('notifications.actions.next', { count: pendingCount }) }}
            </template>
            <template v-else>
              {{ $t('notifications.rewardCard.buttonClose') }}
            </template>
          </button>
          <p v-if="nextRewardHint" class="text-[12px] text-ink-3 text-center px-4">
            {{ nextRewardHint }}
          </p>
        </div>
      </div>
    </div>
  </AppSheet>
</template>

<script setup lang="ts">
import { computed, watch, nextTick, ref } from 'vue'
import AppSheet from '@/components/common/AppSheet.vue';
import confetti from 'canvas-confetti'
import { feedbackService } from '@/services/feedback.service'
import ConcentricCirclesImage from '@/components/common/ConcentricCirclesImage.vue'
import LucideIcon from '@/components/common/LucideIcon.vue'
import type { RewardNotification } from '@/composables/useRewardNotification'
import { useNotificationQueue } from '@/composables/useNotificationQueue'
import { useHaptics } from '@/composables/useHaptics'
import { useI18n } from 'vue-i18n'
import { getChildColorInfo, DEFAULT_CHILD_COLOR } from '@/lib/colors'
import { useRewardsStore } from '@/stores/rewards'
import type { Child } from '@/models/Child'

interface Props {
  notification: RewardNotification | null
  child?: Child | null
  isOnboarding?: boolean
}

const props = defineProps<Props>()

const emit = defineEmits<{
  dismiss: []
}>()

const { pendingCount: queuePendingCount } = useNotificationQueue()
const haptics = useHaptics()
const { t } = useI18n()
const rewardsStore = useRewardsStore()

const isExplicitDismiss = ref(false)

// Track whether the currently displayed notification is a real reward claim.
// We only piggyback the App Store review prompt on `reward` notifications
// (the genuine "child claimed and parent delivered" emotional peak), not on
// level-ups, avatar unlocks, or admin messages.
const currentNotifIsRewardClaim = ref(false)

// Feedback entry: shown 2 seconds after modal opens
const showFeedbackEntry = ref(false)
watch(() => props.notification, (notification) => {
  showFeedbackEntry.value = false
  currentNotifIsRewardClaim.value = notification?.type === 'reward'
  if (notification) {
    setTimeout(() => { showFeedbackEntry.value = true }, 2000)
  }
})

const isOpen = computed(() => !!props.notification)
const pendingCount = computed(() => queuePendingCount.value)
const hasMoreNotifications = computed(() => pendingCount.value > 0)

// ── Child color palette ────────────────────────────────────────────
const childColor = computed(() => props.child?.color || DEFAULT_CHILD_COLOR.value)
const palette = computed(() => getChildColorInfo(childColor.value))

// ── Avatar / asset image (for animal/pose/accessory/background notifs) ─
const avatarImage = computed(() => {
  if (!props.notification) return undefined
  if (props.notification.image) return props.notification.image
  if (props.notification.reward?.image_url) return props.notification.reward.image_url
  if (props.notification.item) {
    if ('baseImage' in props.notification.item) return props.notification.item.baseImage
    if ('imagePath' in props.notification.item) return props.notification.item.imagePath
  }
  return undefined
})

// ── Title (two-part typography) ────────────────────────────────────
const avatarName = computed(() => {
  if (props.isOnboarding && props.notification?.type === 'task-completion' && props.notification.almostUnlockedReward) {
    return props.notification.almostUnlockedReward.title
  }
  if (props.notification?.reward?.title) return props.notification.reward.title
  if (!props.notification?.item) return ''
  if ('name' in props.notification.item) return props.notification.item.name
  return ''
})

const avatarDescription = computed(() => {
  if (props.notification?.reward?.description) return props.notification.reward.description
  if (!props.notification?.item) return undefined
  if ('description' in props.notification.item) return props.notification.item.description
  return undefined
})

const titleMain = computed(() => {
  if (props.notification?.type === 'level' && props.notification.level !== undefined) {
    return t('notifications.rewardCard.level', { level: props.notification.level })
  }
  return avatarName.value
})

const titleVerb = computed(() => {
  if (!props.notification?.type) return ''
  return t(`notifications.rewardCard.unlockVerb.${props.notification.type}`)
})

// ── Eyebrow ────────────────────────────────────────────────────────
const eyebrowLabel = computed(() => {
  if (!props.notification?.type) return ''
  return t(`notifications.rewardCard.eyebrow.${props.notification.type}`)
})

// ── Subtitle ───────────────────────────────────────────────────────
const subtitle = computed(() => {
  if (!props.notification) return ''
  const name = props.child?.name ?? ''

  if (avatarDescription.value) return avatarDescription.value

  switch (props.notification.type) {
    case 'reward':
    case 'custom':
      if (props.notification.message) return props.notification.message
      return t('notifications.rewardCard.subtitleReward', {
        points: props.child?.points ?? 0,
        name,
      })
    case 'level':
      return t('notifications.rewardCard.subtitleLevel', {
        name,
        level: props.notification.level ?? 0,
      })
    case 'animal':
    case 'pose':
    case 'accessory':
    case 'background':
      return t('notifications.rewardCard.subtitleAvatar', { name })
    default:
      return ''
  }
})

// ── Next-reward hint (footer) ──────────────────────────────────────
const nextRewardHint = computed(() => {
  const type = props.notification?.type
  if (!type || !['reward', 'level'].includes(type)) return null

  const childPoints = props.child?.points ?? 0
  const futureRewards = rewardsStore.rewards
    .filter((r: any) => (r.required_points ?? 0) > childPoints)
    .sort((a: any, b: any) => (a.required_points ?? 0) - (b.required_points ?? 0))

  const next = futureRewards[0]
  if (!next) return null
  return t('notifications.rewardCard.nextHint', { points: next.required_points })
})

// ── Central content selector ──────────────────────────────────────
const showCentralAvatarImage = computed(() =>
  !!avatarImage.value &&
  ['animal', 'pose', 'accessory', 'background'].includes(props.notification?.type || ''),
)

const showCentralLevelNumber = computed(() =>
  props.notification?.type === 'level' && props.notification.level !== undefined,
)

const centralIconName = computed(() => {
  const type = props.notification?.type
  const map: Record<string, string> = {
    reward: 'Gift',
    custom: 'MessageCircleHeart',
    'task-completion': 'Gift',
  }
  return map[type ?? ''] || 'Gift'
})

// ── Celebration effects ────────────────────────────────────────────
// Le CSS respecte prefers-reduced-motion tout seul ; les effets déclenchés en
// JS (confetti, vibration longue) doivent le vérifier eux-mêmes, sinon le
// moment le plus joyeux de l'app devient le plus hostile pour un utilisateur
// à sensibilité vestibulaire. On garde l'haptique de succès courte : c'est un
// retour tactile, pas du mouvement à l'écran.
const prefersReducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches

watch(isOpen, async (newValue) => {
  if (newValue) {
    isExplicitDismiss.value = false
    await nextTick()

    if (props.isOnboarding) return

    if (prefersReducedMotion()) {
      void haptics.notificationSuccess()
      return
    }

    haptics.vibrate(600).then(() => {
      setTimeout(() => haptics.notificationSuccess(), 200)
    })

    setTimeout(() => {
      triggerConfetti()
    }, 300)
  }
})

/**
 * canvas-confetti pose son canevas sur `<body>` en `z-index: 100`. Nos
 * surcouches vivent dans les 19990+ : les confettis tombaient donc DERRIÈRE le
 * panneau de célébration, qui est opaque, et personne ne les voyait jamais.
 *
 * 19995 les met au-dessus du panneau (19991) mais sous les boîtes de dialogue
 * (20000) : si une confirmation devait apparaître pendant une célébration,
 * c'est elle qui doit rester lisible.
 */
const CONFETTI_Z_INDEX = 19995

function triggerConfetti() {
  const p = palette.value
  const colors = [p.hex300, p.hex500, p.hex900, '#FFD700', '#FFFFFF']

  for (let i = 0; i < 3; i++) {
    setTimeout(() => {
      confetti({
        particleCount: 50,
        angle: 90,
        spread: 70,
        origin: { x: 0.5, y: 0.6 },
        colors: colors,
        zIndex: CONFETTI_Z_INDEX,
      })
    }, i * 150)
  }
}

function handleButtonClick() {
  maybeTriggerReviewPrompt()
  emit('dismiss')
}

function handleDismiss() {
  if (isExplicitDismiss.value) {
    isExplicitDismiss.value = false
    maybeTriggerReviewPrompt()
    emit('dismiss')
  }
}

function maybeTriggerReviewPrompt() {
  if (!currentNotifIsRewardClaim.value) return
  currentNotifIsRewardClaim.value = false
  // Defer so the celebration modal finishes its dismiss animation before the
  // feedback sheet rises — avoids two modals fighting over the presenting page.
  setTimeout(() => {
    feedbackService.recordRewardDelivered()
  }, 600)
}
</script>

<style>
.celebration-backdrop {
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
}

/* Sur grand écran, la prise de plein écran est disproportionnée : la
   composition est dessinée pour un cadre de téléphone et se retrouve à flotter
   au milieu d'une pièce vide, avec un bouton de 450px pour seul repère. Elle
   devient une carte centrée, aux proportions d'origine. Le fond flouté
   continue de prendre toute la fenêtre, donc le moment garde sa force
   d'interruption : c'est la boîte qui rétrécit, pas l'événement.
   Le plancher de hauteur laisse le téléphone en paysage en plein écran, où une
   carte de 680px ne tiendrait pas. */
@media (min-width: 768px) and (min-height: 600px) {
  .app-sheet-panel.reward-celebration-panel {
    inset: auto;
    left: 50%;
    top: 50%;
    width: 420px;
    height: min(680px, 86vh);
    transform: translate(-50%, -50%);
    border-radius: 20px;
    overflow: hidden;
  }
}
</style>
